import React from 'react';
import axios from 'axios';
import ReactGA4 from 'react-ga4';

import { API_PATH_SAVEWORKBOOK, API_PATH_SAVEWORKBOOK_HANZI, API_PATH_SAVEWORKBOOK_CHENGYU } from './constants';


const WORKBOOK_URL = 'https://payhip.com/b/pb0hd';
const WORKBOOK_COUPON = 'HSKLEVEL15';
const WORKBOOK_TITLE = 'Mandarin Grammar Workbook, Volume 1: Building Blocks';

/* A/B variants. To run a new test, add an entry here: the assignment and the
   tracking follow automatically. Only one variant is live for now. */
const WORKBOOK_VARIANTS = [
	{id: 'a'}
];

/* Shown on the characters and chengyu results, where no HSK level is computed */
const WORKBOOK_COPY_GENERIC = {
	headline: 'A grammar workbook for the foundations',
	body: <>Vocabulary and characters are only half of it: at some point the sentences have to hold together. <em>Building Blocks</em> is 174 pages of HSK 1 grammar practice, 108 exercises, every answer explained, and a companion Anki deck of 534 sentences.</>,
	showCoupon: true,
	showInline: false
};

/* One entry per HSK 3.1 level, index 0 meaning below HSK 1 */
const WORKBOOK_COPY = [
	{
		headline: 'You are right at the very beginning',
		body: <>That is genuinely the best place to be, because nothing has to be unlearned yet. I wrote <em>Building Blocks</em> for exactly this moment: eighteen short chapters that walk through HSK 1 grammar from zero, 108 exercises, and an answer key that explains the reasoning instead of just giving you the answer.</>,
		showCoupon: true,
		showInline: true
	},
	{
		headline: 'Your result puts you at HSK 1',
		body: <>This is the level I built the whole book around. You already know the words, so what is usually missing is the practice that turns them into sentences: 108 exercises on the new HSK 1 standard, and an Anki deck of 534 sentences so you review grammar in context rather than in isolation.</>,
		showCoupon: true,
		showInline: true
	},
	{
		headline: 'Your result puts you at HSK 2',
		body: <>You have moved past HSK 1 vocabulary, and in my experience teaching beginners, that is exactly when the early grammar quietly stops being solid. <em>Building Blocks</em> is a clean pass back through those foundations, with a full answer key so you can find the gaps instead of guessing where they are.</>,
		showCoupon: true,
		showInline: true
	},
	{
		headline: 'Your result puts you at HSK 3',
		body: <>Most learners I teach reach this point knowing far more words than they can confidently put into sentences. That gap is the reason I wrote this book: 108 exercises on the HSK 1 patterns everything else is built on, with every answer explained.</>,
		showCoupon: true,
		showInline: true
	},
	{
		headline: 'Your result puts you at HSK 4',
		body: <>At your level the missing pieces are almost never vocabulary. They tend to be the basic patterns nobody ever drilled properly, and they quietly limit how natural your Chinese sounds. <em>Building Blocks</em> is a foundations check you can work through quickly, and the free chapter below will tell you whether that applies to you.</>,
		showCoupon: true,
		showInline: true
	},
	{
		headline: 'This one is probably below your level',
		body: <><em>Building Blocks</em> covers HSK 1 grammar, so I doubt it is for you. I mention it in case you teach, tutor, or know someone who is just starting out. The free chapter will tell you in two minutes whether it is any good.</>,
		showCoupon: true,
		showInline: false
	},
	{
		headline: 'You are well past this one',
		body: <><em>Building Blocks</em> is my HSK 1 grammar workbook, comfortably below where you are now. It is worth knowing about mainly if you are helping someone begin. There is a free chapter below if you are curious how I put it together.</>,
		showCoupon: true,
		showInline: false
	},
	{
		headline: 'Not for you, but perhaps for someone you know',
		body: <><em>Building Blocks</em> is a beginner grammar workbook I wrote around the new HSK 1 standard. You are long past it, but if you teach or tutor, the exercise sets and the explained answer keys might save you some preparation time.</>,
		showCoupon: true,
		showInline: false
	}
];


/* djb2, so that the same email always gets the same variant. Each retake
   creates a new user id, but the email stays the same. */
function hashEmail(email) {
	let hash = 5381;
	const cleanEmail = String(email).trim().toLowerCase();
	for(let i=0; i<cleanEmail.length; i++) {
		hash = ((hash * 33) ^ cleanEmail.charCodeAt(i)) >>> 0;
	}
	return hash;
}

function chooseVariant(email) {
	try {
		return WORKBOOK_VARIANTS[hashEmail(email) % WORKBOOK_VARIANTS.length];
	}
	catch(error) {
		return WORKBOOK_VARIANTS[0];
	}
}

function workbookLink(variantId, medium) {
	return WORKBOOK_URL + '?utm_source=hsklevel&utm_medium=' + medium
		+ '&utm_campaign=workbook_v1&utm_content=variant_' + variantId;
}


class WorkbookAd extends React.Component {
	constructor(props) {
		super(props);

		this.state = {variant: chooseVariant(props.userEmail)};
	}

	componentDidMount() {
		/* Only the main block records the impression, so that the short mention
		   further down the page does not count the same view twice */
		if(this.props.display === 'inline') return;

		this.save(null);
		try {
			ReactGA4.event('workbook_impression', {
				variant: this.state.variant.id,
				level: this.props.hsk31level
			});
		}
		catch(error) {}
	}

	save(workbookClick) {
		if(this.props.userId === null || this.props.userId === undefined) return;

		/* Each test has its own table, so the id must go to the matching endpoint */
		const path = this.props.testType === 'hanzi' ? `${API_PATH_SAVEWORKBOOK_HANZI}`
			: (this.props.testType === 'chengyu' ? `${API_PATH_SAVEWORKBOOK_CHENGYU}` : `${API_PATH_SAVEWORKBOOK}`);

		axios({
			method: 'post',
			url: path,
			headers: {'content-type': 'application/json'},
			data: {userId: this.props.userId, workbookVariant: this.state.variant.id, workbookClick: workbookClick}
		})
		.catch(error => {});
	}

	click(e, destination) {
		this.save(destination);
		try {
			ReactGA4.event('workbook_click', {
				variant: this.state.variant.id,
				level: this.props.hsk31level,
				destination: destination
			});
		}
		catch(error) {}
	}

	render() {
		/* Anything unexpected about the level and the block simply disappears,
		   the results themselves are never at risk */
		let copy;
		if(this.props.generic) {
			copy = WORKBOOK_COPY_GENERIC;
		}
		else {
			const level = parseInt(this.props.hsk31level, 10);
			if(isNaN(level) || level < 0 || level >= WORKBOOK_COPY.length) return null;
			copy = WORKBOOK_COPY[level];
		}
		const variantId = this.state.variant.id;

		if(this.props.display === 'inline') {
			if(!copy.showInline) return null;
			return (
				<p><em>
					{'This is the level my grammar workbook covers. '}
					<a href={workbookLink(variantId, 'result_page_hsk31')} className="alert-link"
						onClick={(e) => this.click(e, 'buy_inline')}
						target="_blank" rel="noreferrer noopener">
						Take a look
					</a>
				</em></p>
			);
		}

		return (
		<div className="alert alert-primary workbook-panel">
			<a href={workbookLink(variantId, 'result_page_cover')} onClick={(e) => this.click(e, 'buy_cover')}
				target="_blank" rel="noreferrer noopener" className="workbook-cover">
				<img src="workbook-cover.png" width="180px" alt={WORKBOOK_TITLE} />
			</a>
			<div style={{flex:'1 1 260px'}}>
				<strong>{copy.headline}</strong>
				<br/>
				<em style={{fontSize:'16px'}}>{WORKBOOK_TITLE}</em>
				<p style={{marginTop:'8px', marginBottom:'8px'}}>
					{copy.body}
				</p>
				{copy.showCoupon &&
					<p style={{marginTop:'8px', marginBottom:'8px'}}>
						Enter the coupon code <strong>{WORKBOOK_COUPON}</strong> at checkout for 15% off, as a thank you for taking the test.
					</p>
				}
				<button className="btn btn-primary" style={{margin:'5px 5px 5px 5px'}} type="button">
					<a href={workbookLink(variantId, 'result_page')} onClick={(e) => this.click(e, 'buy_top')}
						target="_blank" rel="noreferrer noopener"
						style={{color:'white', textDecoration:'none', fontSize:'16px'}}>
						See the workbook
					</a>
				</button>
				<a href="workbook-preview-chapter.pdf" onClick={(e) => this.click(e, 'preview_top')}
					target="_blank" rel="noreferrer noopener"
					style={{color:'#4A6FA5', fontSize:'16px'}}>
					Read a free chapter
				</a>
				<p style={{marginTop:'8px', marginBottom:'0'}}>
					<em style={{fontSize:'16px'}}>I{"'"}m Pierre. I built this test, and I wrote the workbook.</em>
				</p>
			</div>
		</div>
		);
	}
}


export default WorkbookAd
